/**
 * Pure helpers for Importdekning-seksjonen — typer + `computeCoverage`.
 * Bevisst i egen fil uten Supabase-import så vitest kan importere
 * funksjonen direkte uten å initialisere Supabase-klienten (CI har ikke
 * `VITE_SUPABASE_URL` for unit-jobben).
 */

import { formatDate } from "@/lib/format";

export type SyncSource = "monday" | "poweroffice";
export type SyncTriggerSource = "cron" | "manual" | "backfill" | "unknown";

export interface SyncMetadata {
  triggered_by?: SyncTriggerSource;
  from_date?: string;
  to_date?: string;
  project_codes?: string[];
  user_ids?: string[];
  board_ids?: number[];
  project_ids?: string[];
  mode?: string;
  project_count?: number;
  subitem_board_id?: number;
  /** Prosjekt-koder som fikk full backfill (fromDate=2020-01-01) i tillegg
   *  til ordinær incremental sync. Settes for nye prosjekter opprettet
   *  etter forrige vellykkede kjøring. */
  backfill_project_codes?: string[];
  /** ISO-dato — fromDate brukt for backfill-grenen (typisk 2020-01-01). */
  backfill_from_date?: string;
}

export interface DBSyncLog {
  id: string;
  source: SyncSource;
  entity_type: string;
  status: "running" | "success" | "error";
  records_synced: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  metadata: SyncMetadata;
}

export type SyncHealthStatus =
  | "healthy"
  | "stale"
  | "warning"
  | "critical"
  | "never_run";

export interface GapWarning {
  kind:
    | "project_created_after_first_sync"
    | "no_recent_sync"
    | "cron_stalled";
  message: string;
  project_id?: string;
  detected_at: string;
}

export interface SyncCoverage {
  source: SyncSource;
  entity_type: string;
  health_status: SyncHealthStatus;
  last_run_at: string | null;
  last_status: DBSyncLog["status"] | null;
  last_records_synced: number | null;
  last_error: string | null;
  /** MIN(metadata.from_date) over vellykkede runs. */
  coverage_from: string | null;
  /** MAX(metadata.to_date) over vellykkede runs. */
  coverage_to: string | null;
  /** Project_codes / user_ids / project_ids fra siste vellykkede run. */
  projects_in_last_run: string[] | null;
  last_triggered_by: SyncTriggerSource | null;
  total_runs: number;
  errors_last_24h: number;
  gap_warnings: GapWarning[];
}

export interface ProjectMeta {
  id: string;
  name: string;
  project_code: string | null;
  created_at: string;
}

/**
 * Hver entity-type forventet sync-frekvens (millisekunder). Brukes til
 * `cron_stalled`-deteksjon. Verdiene er konservative — mer for å fange opp
 * helt døde cron-jobber enn for å varsle om småforsinkelser.
 */
// Monday-syncer er manuell-bare (ingen cron) — settes høyt nok til at
// stale-deteksjon ikke trigger ved normal bruk. PowerOffice er cron og
// beholder kort intervall.
const EXPECTED_INTERVAL_MS: Record<string, number> = {
  "poweroffice:vouchers": 4 * 60 * 60 * 1000,
  "poweroffice:voucher_approvals": 30 * 60 * 1000,
  "poweroffice:invoices": 4 * 60 * 60 * 1000,
  "poweroffice:financials": 24 * 60 * 60 * 1000,
  "monday:time_entries": 30 * 24 * 60 * 60 * 1000,
  "monday:projects": 30 * 24 * 60 * 60 * 1000,
  "monday:extras": 30 * 24 * 60 * 60 * 1000,
  "monday:comments": 30 * 24 * 60 * 60 * 1000,
};

/** Sync-typer vi bryr oss om i UI — fast rekkefølge for stabilt grid. */
export const TRACKED_SYNC_TYPES: ReadonlyArray<{
  source: SyncSource;
  entity_type: string;
  label: string;
}> = [
  { source: "poweroffice", entity_type: "vouchers", label: "PowerOffice — Bilag" },
  { source: "poweroffice", entity_type: "voucher_approvals", label: "PowerOffice — Bilagsgodkjenning" },
  { source: "poweroffice", entity_type: "invoices", label: "PowerOffice — Fakturaer" },
  { source: "poweroffice", entity_type: "financials", label: "PowerOffice — GEEK-kalkulering" },
  { source: "monday", entity_type: "time_entries", label: "Monday — Tidsregistreringer" },
  { source: "monday", entity_type: "projects", label: "Monday — Prosjekter" },
  { source: "monday", entity_type: "extras", label: "Monday — Ekstrahonorar" },
  { source: "monday", entity_type: "comments", label: "Monday — Kommentarer" },
];

/**
 * Beregner SyncCoverage for én (source, entity_type) basert på alle
 * sync_log-rader og prosjekter. Eksportert så den kan unit-testes.
 */
export function computeCoverage(
  source: SyncSource,
  entity_type: string,
  logs: DBSyncLog[],
  projects: ProjectMeta[],
): SyncCoverage {
  // Sortér defensivt nyest først så `successful[length-1]` alltid er den
  // eldste vellykkede kjøringen, uavhengig av input-rekkefølgen.
  const relevant = logs
    .filter((l) => l.source === source && l.entity_type === entity_type)
    .slice()
    .sort((a, b) => b.started_at.localeCompare(a.started_at));

  if (relevant.length === 0) {
    return {
      source,
      entity_type,
      health_status: "never_run",
      last_run_at: null,
      last_status: null,
      last_records_synced: null,
      last_error: null,
      coverage_from: null,
      coverage_to: null,
      projects_in_last_run: null,
      last_triggered_by: null,
      total_runs: 0,
      errors_last_24h: 0,
      gap_warnings: [],
    };
  }

  const successful = relevant.filter((l) => l.status === "success");
  const last = relevant[0];

  const coverageFrom = minMetadataDate(successful, "from_date");
  const coverageTo = maxMetadataDate(successful, "to_date");

  const projectsInLastRun = extractProjectsInLastRun(last);
  const lastTrig = (last.metadata?.triggered_by as SyncTriggerSource) ?? "unknown";

  const errors24h = countErrorsLast24h(relevant);
  const healthStatus = computeHealthStatus(
    last,
    successful[0] ?? null,
    `${source}:${entity_type}`,
    errors24h,
  );

  const gapWarnings = computeGapWarnings(
    source,
    entity_type,
    successful,
    projects,
    last,
  );

  return {
    source,
    entity_type,
    health_status: healthStatus,
    last_run_at: last.started_at,
    last_status: last.status,
    last_records_synced: last.records_synced,
    last_error: last.error_message,
    coverage_from: coverageFrom,
    coverage_to: coverageTo,
    projects_in_last_run: projectsInLastRun,
    last_triggered_by: lastTrig,
    total_runs: relevant.length,
    errors_last_24h: errors24h,
    gap_warnings: gapWarnings,
  };
}

function minMetadataDate(rows: DBSyncLog[], key: "from_date" | "to_date"): string | null {
  let min: string | null = null;
  for (const r of rows) {
    const v = r.metadata?.[key];
    if (typeof v === "string" && v !== "all-time") {
      if (!min || v < min) min = v;
    }
    // For from_date: hvis kjøringen også gjorde backfill, telles
    // backfill_from_date som en EKSTRA fra-dato — den er typisk eldre
    // (2020-01-01) og må med i coverage-beregningen.
    if (key === "from_date") {
      const bf = r.metadata?.backfill_from_date;
      if (typeof bf === "string" && bf !== "all-time") {
        if (!min || bf < min) min = bf;
      }
    }
  }
  return min;
}

function maxMetadataDate(rows: DBSyncLog[], key: "from_date" | "to_date"): string | null {
  let max: string | null = null;
  for (const r of rows) {
    const v = r.metadata?.[key];
    if (typeof v === "string" && v !== "all-time") {
      if (!max || v > max) max = v;
    }
  }
  return max;
}

function extractProjectsInLastRun(log: DBSyncLog): string[] | null {
  const m = log.metadata ?? {};
  if (Array.isArray(m.project_codes) && m.project_codes.length > 0) return m.project_codes;
  if (Array.isArray(m.project_ids) && m.project_ids.length > 0) return m.project_ids;
  if (Array.isArray(m.user_ids) && m.user_ids.length > 0) return m.user_ids;
  return null;
}

function countErrorsLast24h(rows: DBSyncLog[]): number {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  return rows.filter(
    (r) => r.status === "error" && new Date(r.started_at).getTime() > cutoff,
  ).length;
}

function computeHealthStatus(
  last: DBSyncLog,
  lastSuccess: DBSyncLog | null,
  expectedKey: string,
  errors24h: number,
): SyncHealthStatus {
  if (last.status === "error") return "critical";
  if (errors24h >= 3) return "warning";

  const expectedInterval = EXPECTED_INTERVAL_MS[expectedKey];
  if (lastSuccess && expectedInterval) {
    const ageMs = Date.now() - new Date(lastSuccess.started_at).getTime();
    if (ageMs > 2 * expectedInterval) return "stale";
  }
  return "healthy";
}

function computeGapWarnings(
  source: SyncSource,
  entity_type: string,
  successful: DBSyncLog[],
  projects: ProjectMeta[],
  last: DBSyncLog,
): GapWarning[] {
  const warnings: GapWarning[] = [];
  const now = new Date().toISOString();

  // 1. Cron-stalled — siste vellykket sync er for gammel
  const expectedKey = `${source}:${entity_type}`;
  const expectedInterval = EXPECTED_INTERVAL_MS[expectedKey];
  const lastSuccess = successful[0];
  if (lastSuccess && expectedInterval) {
    const ageMs = Date.now() - new Date(lastSuccess.started_at).getTime();
    if (ageMs > 2 * expectedInterval) {
      const ageHours = Math.round(ageMs / (60 * 60 * 1000));
      warnings.push({
        kind: "cron_stalled",
        message: `Sist vellykket kjøring var ${ageHours} timer siden — forventet hver ${Math.round(
          expectedInterval / (60 * 60 * 1000),
        )}. time. Cron kan ha stoppet.`,
        detected_at: now,
      });
    }
  }

  // 2. Project_created_after_first_sync — kun for PO-vouchers/invoices.
  //
  // Prosjekt 555-tilfellet: prosjektet ble opprettet i Sølvrev ETTER første
  // PO-sync. Den syncen som FØRST inkluderte 555 hadde et tidsavgrenset
  // `from_date` (lastSync − 7 dager), som er nyere enn vilkårlige eldre
  // bilag som kan ligge i PO. Da varsler vi at PO kan ha bilag fra før
  // dekningsstarten som vi aldri har hentet.
  //
  // Prosjekter som EKSISTERTE da første sync kjørte er trygge — det første
  // sync-vinduet (typisk 2020-01-01) dekker hele deres historikk.
  if (
    source === "poweroffice" &&
    (entity_type === "vouchers" || entity_type === "invoices") &&
    successful.length > 0
  ) {
    const firstSyncRun = successful[successful.length - 1];
    const firstSyncDate = firstSyncRun.started_at;

    for (const project of projects) {
      if (!project.project_code) continue;
      if (project.created_at < firstSyncDate) continue;

      // Hvis prosjektet noensinne har vært inkludert i en backfill, har
      // vi full historikk for det og skal IKKE varsle om hull.
      const wasBackfilled = successful.some((s) =>
        Array.isArray(s.metadata?.backfill_project_codes) &&
        s.metadata!.backfill_project_codes!.includes(project.project_code!),
      );
      if (wasBackfilled) continue;

      let earliestFromForProject: string | null = null;
      for (const s of successful) {
        const codes = s.metadata?.project_codes;
        if (!Array.isArray(codes)) continue;
        if (!codes.includes(project.project_code)) continue;
        const f = s.metadata?.from_date;
        if (typeof f !== "string" || f === "all-time") continue;
        if (!earliestFromForProject || f < earliestFromForProject) {
          earliestFromForProject = f;
        }
      }
      if (!earliestFromForProject) continue;
      if (earliestFromForProject <= "2020-01-01") continue;

      warnings.push({
        kind: "project_created_after_first_sync",
        message: `${project.name} (kode ${project.project_code}, opprettet ${formatDate(project.created_at)}) — synket fra ${formatDate(earliestFromForProject)}. Bilag før den datoen kan mangle.`,
        project_id: project.id,
        detected_at: now,
      });
    }
  }

  // 3. No_recent_sync — siste run total er VELDIG gammel (24h+)
  const lastAge = Date.now() - new Date(last.started_at).getTime();
  if (lastAge > 24 * 60 * 60 * 1000) {
    const ageHours = Math.round(lastAge / (60 * 60 * 1000));
    warnings.push({
      kind: "no_recent_sync",
      message: `Sist kjøring var ${ageHours} timer siden.`,
      detected_at: now,
    });
  }

  return warnings;
}
